import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ChatCircleDots, Microphone, Star, Quotes, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function MentorProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get(`/mentors/${id}`).then((r) => setM(r.data)).catch(() => {});
  }, [id]);

  if (!m) return <div className="min-h-screen flex items-center justify-center text-zinc-500">…</div>;

  const testimonials = [
    { name: "Imani K.", text: "Cut my product roadmap in half. Way more clarity." },
    { name: "Daichi T.", text: "Felt like a real mentor. Ask me anything energy." },
    { name: "Sade O.", text: "Helped me ship in 9 days what I'd been stuck on for a year." },
  ];

  return (
    <div className="min-h-screen bg-[#050505] pb-40" data-testid="mentor-profile">
      <div className="relative h-[58vh] w-full overflow-hidden">
        <img src={m.avatar} alt={m.name} className="w-full h-full mentor-portrait-hero" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />

        <button
          onClick={() => nav(-1)}
          data-testid="back-btn"
          className="absolute top-12 left-5 w-10 h-10 rounded-full glass flex items-center justify-center active:scale-95"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/70">{m.domain}</div>
          <h1 className="mt-1 font-display text-5xl font-light tracking-tight">{m.name}</h1>
          <div className="mt-1 text-zinc-300 text-sm">{m.title}</div>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><Star size={12} weight="fill" className="text-amber-300" /> {m.rating}</span>
            <span className="text-zinc-400">{m.learners.toLocaleString()} learners</span>
            <span className="text-zinc-400">{m.style}</span>
          </div>
        </div>
      </div>

      <div className="px-6 -mt-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-3xl p-5"
        >
          <p className="text-zinc-300 text-sm leading-relaxed">{m.tagline}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {m.expertise.map((e) => (
              <span key={e} className="text-[10px] uppercase tracking-wider bg-white/5 border border-white/10 px-2.5 py-1 rounded-full text-zinc-200">{e}</span>
            ))}
          </div>
        </motion.div>

        <div className="mt-5 grid grid-cols-5 gap-3">
          <Link
            to={`/chat/${m.id}`}
            data-testid="start-chat-btn"
            className="col-span-4 rounded-full bg-white text-black font-medium py-4 flex items-center justify-center gap-2 active:scale-[0.98] transition"
          >
            <ChatCircleDots size={18} weight="fill" /> Start chat
          </Link>
          <button
            data-testid="voice-btn"
            className="rounded-full glass-strong text-white py-4 flex items-center justify-center active:scale-[0.98] transition"
          >
            <Microphone size={18} weight="fill" />
          </button>
        </div>

        <div className="mt-8">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Learning modules</h3>
          <div className="space-y-2">
            {m.modules.map((mod, i) => (
              <div key={mod} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`module-${i}`}>
                <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center text-xs font-mono-jb text-zinc-400">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1 text-sm">{mod}</div>
                <div className="text-[10px] text-zinc-500">~{8 + i * 3}m</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Outcomes</h3>
          <div className="glass rounded-2xl p-5 space-y-2.5">
            {["Ship a tangible artefact within 14 days", "Build a peer network of 5+ peers", "Develop a personal scoreboard"].map((o) => (
              <div key={o} className="flex items-start gap-2.5 text-sm text-zinc-200">
                <Check size={14} weight="bold" className="text-emerald-400 mt-1 shrink-0" />
                <span>{o}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3">What learners say</h3>
          <div className="space-y-2">
            {testimonials.map((t) => (
              <div key={t.name} className="glass rounded-2xl p-4">
                <Quotes size={14} weight="fill" className="text-zinc-500 mb-2" />
                <p className="text-sm text-zinc-200 italic leading-relaxed">{t.text}</p>
                <div className="mt-2 text-[11px] text-zinc-500">— {t.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
